import { PlinkoLayout, PlinkoPeg } from "./plinko_layout.js";

export interface PlinkoBody
{
  x: number;
  y: number;
  vx: number;
  vy: number;
  idle_ms: number;
  impact: number;
}

export interface PlinkoDrop
{
  x: number;
  seed: number;
}

export class PlinkoPhysics
{
  static readonly STEP_S = 1 / 240;

  private static readonly GRAVITY = 3600;
  private static readonly PEG_RESTITUTION = 0.4;
  private static readonly WALL_RESTITUTION = 0.5;
  private static readonly PEG_KICK = 80;
  private static readonly SPAWN_JITTER = 6;
  private static readonly MAX_SPEED = 2600;
  private static readonly STUCK_SPEED = 40;
  private static readonly STUCK_MS = 700;
  private static readonly MAX_STEPS = 240 * 20;
  private static readonly AIM_TRIES = 400;
  private static readonly PREFER_TRIES = 100;
  private static readonly SHIFT_TRIES = 60;
  private static readonly SHIFT_STEP = 20;

  private static readonly pegs: PlinkoPeg[] = PlinkoLayout.pegs();

  static random( seed: number ): () => number
  {
    let state = seed >>> 0;
    return () =>
    {
      state = ( state + 0x6D2B79F5 ) >>> 0;
      let t = state;
      t = Math.imul( t ^ ( t >>> 15 ), t | 1 );
      t ^= t + Math.imul( t ^ ( t >>> 7 ), t | 61 );
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  static spawn( drop: PlinkoDrop, random: () => number ): PlinkoBody
  {
    return {
      x: drop.x + ( random() * 2 - 1 ) * PlinkoPhysics.SPAWN_JITTER,
      y: PlinkoLayout.holeCenterY(),
      vx: 0,
      vy: 0,
      idle_ms: 0,
      impact: 0,
    };
  }

  static step( body: PlinkoBody, random: () => number ): boolean
  {
    const dt = PlinkoPhysics.STEP_S;
    const r = PlinkoLayout.BALL_RADIUS;

    body.impact = 0;
    body.vy += PlinkoPhysics.GRAVITY * dt;

    const speed = Math.hypot( body.vx, body.vy );
    if ( speed > PlinkoPhysics.MAX_SPEED )
    {
      body.vx *= PlinkoPhysics.MAX_SPEED / speed;
      body.vy *= PlinkoPhysics.MAX_SPEED / speed;
    }

    body.x += body.vx * dt;
    body.y += body.vy * dt;

    for ( const peg of PlinkoPhysics.pegs )
      PlinkoPhysics.collideCircle( body, peg.x, peg.y, PlinkoLayout.PEG_RADIUS, random );

    for ( let i = 0; i < PlinkoLayout.DIVIDER_COUNT; i++ )
    {
      const divider_x = PlinkoLayout.dividerX( i );
      const top_y = PlinkoLayout.DIVIDER_TOP + PlinkoLayout.DIVIDER_HALF_W;
      const closest_y = Math.max( top_y, Math.min( PlinkoLayout.FLOOR_Y, body.y ) );
      PlinkoPhysics.collideCircle( body, divider_x, closest_y, PlinkoLayout.DIVIDER_HALF_W, null );
    }

    if ( body.x < PlinkoLayout.WALL_LEFT + r )
    {
      body.x = PlinkoLayout.WALL_LEFT + r;
      body.vx = Math.abs( body.vx ) * PlinkoPhysics.WALL_RESTITUTION;
    }
    else if ( body.x > PlinkoLayout.WALL_RIGHT - r )
    {
      body.x = PlinkoLayout.WALL_RIGHT - r;
      body.vx = -Math.abs( body.vx ) * PlinkoPhysics.WALL_RESTITUTION;
    }

    if ( Math.hypot( body.vx, body.vy ) < PlinkoPhysics.STUCK_SPEED )
    {
      body.idle_ms += dt * 1000;
      if ( body.idle_ms > PlinkoPhysics.STUCK_MS )
      {
        body.vx = ( random() < 0.5 ? -1 : 1 ) * PlinkoPhysics.PEG_KICK * 2;
        body.idle_ms = 0;
      }
    }
    else
      body.idle_ms = 0;

    return body.y + r >= PlinkoLayout.FLOOR_Y;
  }

  static landingSlot( drop: PlinkoDrop ): number
  {
    const random = PlinkoPhysics.random( drop.seed );
    const body = PlinkoPhysics.spawn( drop, random );

    const settled_y = PlinkoLayout.DIVIDER_TOP + PlinkoLayout.BALL_RADIUS;

    for ( let i = 0; i < PlinkoPhysics.MAX_STEPS; i++ )
      if ( PlinkoPhysics.step( body, random ) || body.y > settled_y )
        return PlinkoLayout.slotIndex( body.x );

    return -1;
  }

  static findDrop( aim_x: number, slots: number[] ): PlinkoDrop
  {
    const min_x = PlinkoLayout.holeMinX();
    const max_x = PlinkoLayout.holeMaxX();
    const clamp = ( x: number ) => Math.max( min_x, Math.min( max_x, x ) );
    const start_x = clamp( aim_x );
    const distance = ( slot: number ) => Math.abs( clamp( PlinkoLayout.slotCenterX( slot ) ) - start_x );
    const nearest = slots.reduce( ( best, slot ) => distance( slot ) < distance( best ) ? slot : best );
    const toward = Math.sign( clamp( PlinkoLayout.slotCenterX( nearest ) ) - start_x );

    let drop: PlinkoDrop = { x: start_x, seed: 0 };
    let fallback: PlinkoDrop | null = null;

    for ( let i = 0; i < PlinkoPhysics.AIM_TRIES; i++ )
    {
      drop = { x: start_x, seed: Math.floor( Math.random() * 4294967296 ) };
      const landed = PlinkoPhysics.landingSlot( drop );

      if ( landed === slots[ 0 ] )
        return drop;
      if ( !fallback && slots.includes( landed ) )
        fallback = drop;
      if ( fallback && i >= PlinkoPhysics.PREFER_TRIES )
        return fallback;
    }

    if ( fallback )
      return fallback;

    for ( let shift = PlinkoPhysics.SHIFT_STEP; shift <= max_x - min_x; shift += PlinkoPhysics.SHIFT_STEP )
    {
      const x = clamp( start_x + toward * shift );

      for ( let i = 0; i < PlinkoPhysics.SHIFT_TRIES; i++ )
      {
        drop = { x, seed: Math.floor( Math.random() * 4294967296 ) };
        if ( slots.includes( PlinkoPhysics.landingSlot( drop ) ) )
          return drop;
      }
    }

    return drop;
  }

  private static collideCircle( body: PlinkoBody, cx: number, cy: number, radius: number, random: ( () => number ) | null ): void
  {
    const dx = body.x - cx;
    const dy = body.y - cy;
    const min_dist = PlinkoLayout.BALL_RADIUS + radius;
    const dist_sq = dx * dx + dy * dy;

    if ( dist_sq >= min_dist * min_dist )
      return;

    const dist = Math.sqrt( dist_sq ) || 0.0001;
    const nx = dx / dist;
    const ny = dy / dist;

    body.x = cx + nx * min_dist;
    body.y = cy + ny * min_dist;

    const vn = body.vx * nx + body.vy * ny;
    if ( vn >= 0 )
      return;

    body.vx -= ( 1 + PlinkoPhysics.PEG_RESTITUTION ) * vn * nx;
    body.vy -= ( 1 + PlinkoPhysics.PEG_RESTITUTION ) * vn * ny;

    if ( !random )
      return;

    body.impact = Math.max( body.impact, -vn );
    const push = ( random() * 2 - 1 ) * PlinkoPhysics.PEG_KICK;
    body.vx += -ny * push;
    body.vy += nx * push;
  }
}
